import axios from 'axios';

import { toast } from '@/hooks/use-toast';

import { getEnv } from '../env';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Suppress the global error toast for this request (the caller handles errors in its own UI). */
    skipErrorToast?: boolean;
  }
}

const API_BASE_URL = getEnv('VITE_API_URL');

/**
 * Get the access token from localStorage
 * Use this for non-axios fetch calls to maintain consistency
 */
export function getAccessToken(): string | null {
  return localStorage.getItem('accessToken');
}

/**
 * Handle 401 unauthorized response by attempting token refresh
 * Use this for non-axios fetch calls to maintain consistency with axios interceptors
 */
export async function handleUnauthorized(): Promise<boolean> {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
      const { accessToken } = response.data;
      localStorage.setItem('accessToken', accessToken);
      return true;
    }
  } catch {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/login';
  }
  return false;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

/**
 * Extract error message from API response
 */
function getErrorMessage(error: any): string {
  // Check for NestJS error response format
  if (error.response?.data?.message) {
    const message = error.response.data.message;
    // Handle array of messages (validation errors)
    if (Array.isArray(message)) {
      return message.join(', ');
    }
    return message;
  }

  // Check for generic error message
  if (error.response?.data?.error) {
    return error.response.data.error;
  }

  // Check for status text
  if (error.response?.statusText) {
    return error.response.statusText;
  }

  // Network errors
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.';
  }

  if (error.message === 'Network Error') {
    return 'Unable to connect to the server. Please check your connection.';
  }

  return error.message || 'An unexpected error occurred';
}

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for handling errors and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });

          const { accessToken } = response.data;
          localStorage.setItem('accessToken', accessToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Show toast for all non-401 errors (401 is handled above)
    // Skip toast if the request explicitly disabled it
    if (error.response?.status !== 401 && !originalRequest?.skipErrorToast) {
      const errorMessage = getErrorMessage(error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: errorMessage,
      });
    }

    return Promise.reject(error);
  },
);
